// Constants
import { SensitivityIconType } from "../constants/SensitivityConstants";
import { Button, Menu, MenuPopover, MenuTrigger, Tooltip, mergeClasses, useId } from "@fluentui/react-components";
import { SensitivityButtonMenu } from "./SensitivityButtonMenu";
import { SensitivityIcon } from "./SensitivityIcon";
import { SensitivityJustificationDialog } from "./SensitivityJustificationDialog";
import { SensitivityLabel } from "@ms/planner-models/lib/local/client/tenant/SensitivityLabel";
// Styles
import { useSensitivityButtonStyles } from "./ContentSensitivityLabels.styles";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import * as React from "react";
/**
 * A content sensitivity label component that allows users to view and change
 * the sensitivity classification of the plan.
 *
 * @param props - The component props
 * @returns JSX element representing the sensitivity label control
 */ export const ContentSensitivityLabel = ({ currentLabelId, fileSensitivityLabels, fileSensitivityPolicySettings, onLabelChange, disabled = false })=>{
    const classes = useSensitivityButtonStyles();
    // Extract policy settings for downgrade justification and help URL
    const { downgradeSensitivityRequiresJustification = false, helpWebUrl } = fileSensitivityPolicySettings ?? {};
    const [showContextualMenu, setShowContextualMenu] = React.useState(false);
    const onContextualMenuOpenChange = (_ev, data)=>{
        setTooltipVisible(false);
        setShowContextualMenu(data.open);
    };
    const [tooltipVisible, setTooltipVisible] = React.useState(false);
    const onTooltipVisibleChange = (_ev, data)=>{
        setTooltipVisible(data.visible);
    };
    const buttonRef = React.useRef(null);
    // Controls visibility of the justification dialog (shown for downgrades)
    const [showJustificationDialog, setShowJustificationDialog] = React.useState(false);
    // State to store the currently applied sensitivity label
    const currentLabel = SensitivityLabel.getSensitivityLabel(currentLabelId ?? "", fileSensitivityLabels);
    // State to track the pending label change (for justification dialog)
    const [pendingLabelId, setPendingLabelId] = React.useState(null);
    // Handler to cancel justification dialog
    const cancelJustificationDialog = React.useCallback(()=>{
        setShowJustificationDialog(false);
    }, []);
    const [checkedValues, setCheckedValues] = React.useState({
        label: [
            currentLabelId ?? ""
        ]
    });
    const onCheckedValueChange = (_event, { name, checkedItems })=>{
        setCheckedValues((values)=>values.label[0] !== checkedItems[0] ? {
                ...values,
                [name]: checkedItems
            } : {
                label: [
                    ""
                ]
            });
    };
    // Handler to submit justification and apply the pending label
    const submitJustificationDialog = React.useCallback((justification)=>{
        setShowJustificationDialog(false);
        onLabelChange(pendingLabelId, justification);
    }, [
        pendingLabelId,
        onLabelChange
    ]);
    // Label display name
    const labelDisplay = currentLabel && fileSensitivityLabels.length > 0 ? SensitivityLabel.getFullSensitivityTitle(currentLabel.id, fileSensitivityLabels) : Strings.ContentSensitivityLabel_NoLabelTooltip;
    // Handler for when a new sensitivity label is selected from the menu
    const onApplyLabel = (label)=>{
        // Check if downgrade requires justification
        if (downgradeSensitivityRequiresJustification && currentLabel && label.priority <= currentLabel.priority) {
            setPendingLabelId(label.priority === currentLabel.priority ? null : label.id);
            setShowJustificationDialog(true); // Show justification dialog for downgrades
            return;
        }
        // Apply label immediately if no justification needed
        onLabelChange(label.id === currentLabel?.id ? null : label.id);
    };
    // Determine the appropriate icon type based on label state and protection level
    const labelIconType = ()=>{
        if (!currentLabel) {
            return SensitivityIconType.TagUnknown; // No label applied
        }
        if (currentLabel.hasProtection) {
            return SensitivityIconType.TagProtected; // Protected label
        }
        return SensitivityIconType.Tag; // Standard classification label
    };
    const labelDescriptionId = useId("sensitivity-label-description");
    // Description text for screen readers
    const labelDescription = currentLabel ? currentLabel.toolTip : Strings.ContentSensitivityLabel_NoLabelTooltipDescription;
    const tooltipProps = {
        content: {
            className: classes.tooltipContainer,
            children: /*#__PURE__*/ React.createElement("div", {
                className: classes.tooltipWrapper,
                "data-testid": "sensitivity-label-tooltip-content"
            }, /*#__PURE__*/ React.createElement("div", {
                className: classes.tooltipTitle,
                "data-testid": "sensitivity-label-tooltip-title"
            }, labelDisplay), /*#__PURE__*/ React.createElement("div", {
                id: labelDescriptionId,
                "data-testid": "sensitivity-label-tooltip-description"
            }, labelDescription))
        }
    };
    const menuId = useId();
    if (!fileSensitivityLabels.length) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classes.wrapper,
        "data-testid": "content-sensitivity-label"
    }, /*#__PURE__*/ React.createElement(Tooltip, {
        ...tooltipProps,
        withArrow: true,
        positioning: "below",
        visible: !showContextualMenu && tooltipVisible,
        onVisibleChange: onTooltipVisibleChange,
        relationship: "description",
        "data-testid": "sensitivity-label-tooltip"
    }, /*#__PURE__*/ React.createElement("div", {
        className: classes.buttonWrapper
    }, /*#__PURE__*/ React.createElement(Menu, {
        hasIcons: true,
        open: showContextualMenu,
        onOpenChange: onContextualMenuOpenChange,
        checkedValues: checkedValues,
        onCheckedValueChange: onCheckedValueChange
    }, /*#__PURE__*/ React.createElement(MenuTrigger, null, /*#__PURE__*/ React.createElement(Button, {
        appearance: "subtle",
        icon: {
            className: classes.sensitivityButtonIcon,
            children: /*#__PURE__*/ React.createElement(SensitivityIcon, {
                iconType: labelIconType(),
                color: currentLabel?.color
            })
        },
        ref: buttonRef,
        className: mergeClasses(classes.sensitivityButton),
        onClick: ()=>setShowContextualMenu(!showContextualMenu),
        "aria-expanded": showContextualMenu,
        "aria-label": labelDisplay,
        "aria-describedby": labelDescriptionId,
        "data-testid": "sensitivity-label-button",
        disabled: disabled
    })), /*#__PURE__*/ React.createElement(MenuPopover, {
        className: mergeClasses(classes.tooltipContainer, classes.menuPopover),
        "data-testid": "sensitivity-label-menu"
    }, /*#__PURE__*/ React.createElement(SensitivityButtonMenu, {
        id: menuId,
        items: fileSensitivityLabels,
        currentLabel: currentLabel,
        onApplyLabel: onApplyLabel,
        moreInfoUrl: helpWebUrl,
        checkedValues: checkedValues,
        onCheckedValueChange: onCheckedValueChange
    }))))), /*#__PURE__*/ React.createElement(SensitivityJustificationDialog, {
        sensitivityButtonRef: buttonRef,
        open: showJustificationDialog,
        onCancelled: cancelJustificationDialog,
        onSubmitted: submitJustificationDialog,
        moreInfoUrl: helpWebUrl
    }));
};
